import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { apiGet } from '../admin/primitives.js'
import { estErreurApi, type CommunicationPeriod, type HonestDelta } from './lib.js'

/**
 * DONNÉES INSTAGRAM — un seul appel `/instagram/insights` partagé par tous
 * les widgets de la page. Changement de période : les données restent
 * affichées pendant le rechargement (pas d'écran vide, pas de spinner —
 * §69, §125), puis les nouvelles valeurs prennent vie via le Motion System.
 */

export interface MetricReading {
  key: string
  label: string
  availability: 'AVAILABLE' | 'UNAVAILABLE' | 'EMPTY_PROVIDER_DATA' | 'NO_DATA'
  value: number | null
  delta: HonestDelta | null
  source: 'INSTAGRAM_INSIGHTS' | 'GESTION_HISTORY'
  detail: string | null
}

export interface ContentArchive {
  state: 'ORIGINAL' | 'PREVIEW_ONLY' | 'NONE'
  preview_asset_id: string | null
  original_asset_id: string | null
  original_mime: string | null
  original_bytes: number | null
  width: number | null
  height: number | null
  duration_ms: number | null
  failed_permanent: boolean
  captured_at: string | null
}

export interface ContentReading {
  id: string
  provider_id: string
  kind: string | null
  caption: string | null
  permalink: string | null
  thumbnail_url: string | null
  media_url: string | null
  published_at: string | null
  metrics: Record<string, number>
  metrics_captured_at: string | null
  like_count: number | null
  comments_count: number | null
  children: { id: string; media_type: string }[]
  archive: ContentArchive
}

/** URL LOCALE d'un asset archivé — le frontend ne parle jamais au CDN Meta. */
export function mediaFileUrl(assetId: string): string {
  return `/api/v1/communication/instagram/media/${assetId}/file`
}

export function mediaDownloadUrl(assetId: string): string {
  return `/api/v1/communication/instagram/media/${assetId}/download`
}

export interface CommentRow {
  id: string
  provider_comment_id: string
  provider_media_id: string
  parent_comment_id: string | null
  author_scoped_id: string | null
  author_username: string | null
  text: string
  like_count: number | null
  hidden: boolean | null
  provider_timestamp: string | null
  missing_since: string | null
}

export interface InsightsOverview {
  connected: boolean
  state: string
  account: { username: string | null; name: string | null; account_type: string | null; connected_at: string | null }
  token: { expires_at: string | null; days_left: number | null; status: string | null }
  lastSyncAt: string | null
  lastError: string | null
  audience: { followers: number | null; delta: HonestDelta | null; series: { day: string; value: number; provenance: string }[] }
  follows: { gained: number | null; lost: number | null; combined: number | null }
  metrics: MetricReading[]
  topContent: ContentReading[]
  reels: ContentReading[]
  stories: ContentReading[]
  demographics: {
    captured_at: string; timeframe: string
    follower: Record<string, Record<string, number>>
    engaged: Record<string, Record<string, number>>
    reached: Record<string, Record<string, number>>
  } | null
  recentComments: (CommentRow & { content_id: string | null; content_kind: string | null })[]
  metricSupport: Record<string, { state: string; detail: string | null }>
}

interface Ctx {
  periode: CommunicationPeriod
  setPeriode: (p: CommunicationPeriod) => void
  data: InsightsOverview | null
  erreur: string | null
  chargement: boolean
  reload: () => void
}

const InstagramCtx = createContext<Ctx | null>(null)

export function useInstagram(): Ctx {
  const v = useContext(InstagramCtx)
  if (!v) throw new Error('useInstagram hors de InstagramDataProvider')
  return v
}

export function metrique(data: InsightsOverview | null, key: string): MetricReading | null {
  return data?.metrics.find((m) => m.key === key) ?? null
}

export function InstagramDataProvider({ children }: { children: ReactNode }) {
  const [periode, setPeriode] = useState<CommunicationPeriod>('SEVEN_DAYS')
  const [data, setData] = useState<InsightsOverview | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [chargement, setChargement] = useState(true)

  const charger = useCallback(async (p: CommunicationPeriod) => {
    setChargement(true)
    try {
      const j = await apiGet<InsightsOverview | { error: string; message?: string }>(
        `/api/v1/communication/instagram/insights?period=${p}`)
      if (estErreurApi(j)) setErreur(j.message ?? 'Lecture impossible.')
      else { setData(j); setErreur(null) }
    } catch {
      setErreur('Gestion n’a pas répondu.')
    } finally {
      setChargement(false)
    }
  }, [])

  useEffect(() => { void charger(periode) }, [periode, charger])

  return (
    <InstagramCtx.Provider value={{ periode, setPeriode, data, erreur, chargement, reload: () => { void charger(periode) } }}>
      {children}
    </InstagramCtx.Provider>
  )
}
